<template>
  <div
    class="main-product component-section critical-component-clear"
    :class="{ [$class('loading')]: !getComponentActive('main-product') || state === 'loading' }"
  >
    <template v-if="locked">
      <page-lock
        :password="liquid.pageLock.password"
        :title="liquid.pageLock.title"
        :sub-title="liquid.pageLock.subTitle"
        :content="liquid.pageLock.content"
        :image="liquid.pageLock.image"
        @page-unlocked="setLockedStatus"
      />
    </template>

    <div
      v-show="!locked"
      class="main-product__wrapper"
    >
      <div class="container main-product__breadcrumbs">
        <breadcrumbs
          :title="product.title || liquid.title"
          :ancestor-title="liquid.ancestorTitle"
          :ancestor-url="liquid.ancestorUrl"
        />
      </div>

      <div class="main-product__gallery">
        <product-gallery
          :product="product"
        />
      </div>

      <div class="main-product__content">
        <div class="main-product__content-wrapper">
          <div
            v-if="readTag('badge') || (!isInitialized && liquid.hasBadge)"
            class="main-product__badge badge"
            v-text="readTag('badge')"
          />

          <h1
            :id="`section-title-${liquid.sectionId}`"
            class="main-product__title h3"
            v-text="getProductTitle"
          />

          <h2
            v-if="getProductSubTitle"
            class="main-product__subtitle h3"
            v-text="getProductSubTitle"
          />

          <p
            v-if="liquid.shortDescription"
            class="main-product__short-description"
            v-text="liquid.shortDescription"
          />

          <div class="main-product__prices-container">
            <product-prices
              :available="product?.available"
              block-class="main-product"
              :compare-at-price="product?.compareAtPrice?.amount"
              :price="giftCardPrice || product?.price?.amount || liquid.price"
              :price-first="false"
              :liquid-is-available="liquid.isAvailable"
              :liquid-compare-at="liquid.hasCompareAt"
              :message="messageTag"
              :is-initialized="isInitialized"
            />
          </div>

          <template v-if="hasSwatches">
            <main-product-swatches
              v-if="swatches.length"
              :label="$string('product.form.color')"
              :product="product"
              :swatches="swatches"
              :pickapack="papData"
              @swatch-updated="handleSwatchUpdate"
              @swatch-loaded="swatchLoaded"
            />
          </template>

          <div class="main-product__form">
            <product-form
              main-product-form
              :liquid="liquid"
              :product="product"
              :variant="variant"
              :pickapack="isPickaPack"
              :has-size-guide="sizeGuide"
              :shop-the-look="false"
              :notify-bis="notifyBis"
              :gift-card="giftCard"
              @update-variant="handleVariantUpdate"
            >
              <template #terms>
                <slot name="payment-terms" />
              </template>
            </product-form>

            <back-in-stock
              v-if="notifyBis"
              :variant-id="variant.id"
              :product-id="product.id"
            />
          </div>

          <div class="main-product__shipping">
            <p
              v-if="liquid.freeShippingEnabled"
              class="main-product__shipping-content"
              v-html="liquid.freeShippingMessage"
            />
            <div class="main-product__shipping-links">
              <page-quick-view
                :title="$string('product.extra.shipping_info')"
                :page="liquid.shippingPage"
              />
              <page-quick-view
                :title="$string('product.extra.returns_policy')"
                :page="liquid.returnsPage"
              />
            </div>
          </div>

          <div class="main-product__description">
            <h5
              class="main-product__description-title"
              v-text="$string('product.form.details')"
            />
            <p v-html="product.description || liquid.description" />
            <p
              v-if="isFinalSale"
              v-html="$string('product.form.final_sale')"
            />
            <p v-html="sku" />

            <ul
              v-if="productHasDescriptors || !isInitialized"
              class="main-product__description-list"
            >
              <li
                v-if="product && product.machineWash && product.machineWash.value !== 'false'"
                class="main-product__description-feature"
              >
                <icon-machine-wash />
                {{ $string('product.extra.machine_wash') }}
              </li>

              <li
                v-if="product.fabric || !isInitialized"
                class="main-product__description-feature"
              >
                <icon-fabric />
                {{ fabricText }}
              </li>

              <li
                v-if="product && product.imported && product.imported.value !== 'false'"
                class="main-product__description-feature"
              >
                <icon-imported />
                {{ $string('product.extra.imported') }}
              </li>
            </ul>
          </div>

          <ul class="main-product__accordions">
            <template
              v-for="accordion of accordions"
              :key="accordion.key"
            >
              <li
                v-if="product[accordion.key] || !isInitialized"
                :class="[
                  `main-product__accordion main-product__accordion--${accordion.key}`,
                  {activeClass: expandedDetails == accordion.key}
                ]"
              >
                <div
                  class="main-product__accordion-header"
                  :class="{activeClass: expandedDetails == accordion.key}"
                  @click="expandDetails(accordion.key)"
                >
                  <span v-text="accordion.label" />
                  <div class="main-product__accordion-icon">
                    <icon-caret-down v-if="expandedDetails != accordion.key" />
                    <icon-caret-up v-else />
                  </div>
                </div>
                <div
                  v-if="product[accordion.key]"
                  v-show="expandedDetails == accordion.key"
                  :class="{activeClass: expandedDetails == accordion.key}"
                  class="main-product__accordion-copy"
                >
                  <p
                    v-if="accordion.type === 'text'"
                    class="main-product__accordion-text"
                    v-html="product[accordion.key].value"
                  />

                  <ul
                    v-else-if="accordion.type === 'list'"
                    class="main-product__accordion-list"
                  >
                    <li
                      v-for="(value, index) of accordion.value"
                      :key="index"
                      v-text="value"
                    />
                  </ul>

                  <styled-with
                    v-else-if="accordion.type === 'styled-with'"
                    :product="product.styledWith.product"
                  />

                  <div
                    v-if="accordion.key === 'sizeFit' && sizeGuide"
                    class="mt-m"
                  >
                    <btn
                      type="button"
                      class="size-guide-link"
                      modifiers="text"
                      :label="$string('product.extra.size_guide')"
                      :show-icon="false"
                      @click="toggleSizeGuide"
                    />
                  </div>
                </div>
              </li>
            </template>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main product
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {String} handle - Product page's original handle.
 * @param {String} liquid.description - Product's Liquid description.
 * @param {Boolean} liquid.hasOnlyDefaultVariant - Has only default variant.
 * @param {String} liquid.price - Product's Liquid price.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.title - Product's Liquid title.
 * @param {String} liquid.fabricPage - Product's fabric page.
 * @param {Object|Boolean} product - CANVAS formatted product object.
 * @param {Object|Boolean} variant - CANVAS formatted variant object.
 * @param {Boolean} hasSwatches - True if product has swatches
 *
 */

import BackInStock from '~async/back-in-stock/back-in-stock'
import Breadcrumbs from '~async/breadcrumbs/breadcrumbs.vue'
import MainProductSwatches from '~async/main-product/main-product-swatches/main-product-swatches'
import PageLock from '~async/page-lock/page-lock.vue'
import PageQuickView from '~async/page-quick-view/page-quick-view'
import ProductForm from '~async/product-form/product-form'
import ProductGallery from '~async/product-gallery/product-gallery'
import StyledWith from '~async/styled-with/styled-with'
import Btn from '~global/btn/btn'
import ProductPrices from '~global/product-prices/product-prices'

import { mapActions, mapGetters, mapState } from 'vuex'
import classes from '~/config/classes'
import productsWithMetafields from '~/graphql/queries/productsWithMetafields.gql'
import productWithMetafields from '~/graphql/queries/productWithMetafields.gql'
import { formatProduct } from '~/helpers/format'
import { handleize } from '~/helpers/general'

import IconCaretDown from '~icons/direction/caret/down.svg'
import IconCaretUp from '~icons/direction/caret/up.svg'
import IconFabric from '~icons/misc/fabric.svg'
import IconImported from '~icons/misc/imported.svg'
import IconMachineWash from '~icons/misc/machine-wash.svg'

export default {
  name: 'MainProduct',

  components: {
    ProductForm,
    MainProductSwatches,
    BackInStock,
    ProductGallery,
    StyledWith,
    ProductPrices,
    PageQuickView,
    PageLock,
    Breadcrumbs,
    Btn,
    IconMachineWash,
    IconFabric,
    IconImported,
    IconCaretDown,
    IconCaretUp,
  },

  props: {
    handle: {
      type: String,
      default: cnvs.page.handle,
    },
    liquid: {
      type: Object,
      default: () => ({
        ancestorTitle: '',
        ancestorUrl: '',
        shippingPage: '',
        returnsPage: '',
        description: '',
        freeShippingEnabled: true,
        freeShippingMessage: '',
        hasOnlyDefaultVariant: false,
        price: 0,
        sectionId: '',
        title: '',
        fabricPage: '',
        papThreshold: '',
        papDiscount: '',
        papCollection: '',
        pageLock: {},
        caastleStagingEnv: false,
        shortDescription: '',
        colorMatchArray: {},
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    hasSwatches: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-request-object': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      expandedDetails: '',
      currentHandle: cnvs.page.handle,
      state: 'loading',
      masterTitle: null,
      gender: null,
      swatchProducts: [],
      swatches: [],
      mainImageLoaded: false,
      selectedSizeIndex: 0,
      locked: false,
      giftCard: window.cnvs.page.handle === 'gift-card',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      componentState: (state) => state.components,
    }),

    /**
     * Compute product Subtitle.
     * @returns {String}
     */
    getProductSubTitle() {
      let defaultColor = ''
      let defaultTitle = ''
      let subTitle = ''
      if (this.product) {
        defaultTitle = this.product.title
      } else {
        defaultTitle = this.liquid.title
      }

      if (defaultTitle.includes(' - ')) {
        defaultColor = defaultTitle.split(' - ')[1].replace(/\s+/g, '').toLowerCase()
      }

      const newColorMatch = this.liquid.colorMatchArray?.find((item) => {
        const matchColors = item.matchColor.map((color) => {
          return color.replace(/\s+/g, '').toLowerCase()
        })
        return matchColors.includes(defaultColor)
      })
      subTitle = newColorMatch ? `${newColorMatch.newColor} ${this.liquid.type}` : ''
      return subTitle
    },

    /**
     * Compute product title.
     * @returns {String}
     */
    getProductTitle() {
      let title = ''

      if (this.product) {
        title = this.product.title

      } else {
        title = this.liquid.title
      }

      if (title.includes(' - ')) {
        title = title.split(' - ')[0]
      }

      return title
    },

    /**
     * Compute active class
     * @returns {Boolean}
     */
    activeClass() {
      return classes.active
    },

    /**
     * Compute if product is tagged with finalsale
     * @returns {Boolean}
     */
    isFinalSale() {

      if (!this.product) {
        return false
      }

      const tag = this.product.tags.find((data) => {
        const clean = data.toLowerCase()
        return clean === 'finalsale'
      })

      return Boolean(tag)
    },

    /**
     * Compute if product is tagged with pickapack
     * @returns {Boolean}
     */
    isPickaPack() {
      if (!this.product) {
        return false
      }

      const tag = this.product.tags.find((data) => {
        const clean = data.toLowerCase()
        return clean === 'pickapack'
      })

      return Boolean(tag)
    },

    /**
     * Compute sku.
     * @returns {String}
     */
    sku() {
      const text = `${this.$string('product.extra.sku')}:`

      if (!this.product) {
        return `${text} ${this.liquid.sku}`
      }

      return `${text} ${this.product.variants[0].sku}`
    },

    /**
     * Format Pickapack data.
     * @returns {Object}
     */
    papData() {
      return {
        threshold: this.liquid.papThreshold,
        discount: this.liquid.papDiscount,
        collection: this.liquid.papCollection,
      }
    },

    /**
     * Compute whether GQL data is present.
     * @returns {Boolean}
     */
    isInitialized() {
      return Boolean(this.product)
    },

    /**
     * Compute whether product has descriptor meta values.
     * @returns {Boolean}
     */
    productHasDescriptors() {
      if (!this.product) {
        return false
      }

      return (
        (this.product.imported && this.product.imported.value !== 'false') ||
        (this.product.machineWash && this.product.machineWash.value !== 'false') ||
        this.product.fabric
      )
    },

    /**
     * Compute value of fabric metafield or return placeholder.
     * @returns {String}
     */
    fabricText() {
      if (this.product && this.product.fabric) {
        return this.product.fabric.value
      }

      return this.$string('product.extra.fabric')
    },

    /**
     * Compute relevant data to render in accordions.
     * @returns {Array}
     */
    accordions() {
      if (!this.isInitialized) {
        return [
          {
            key: 'careGuide',
            label: this.$string('product.form.care'),
            value: '',
          },
          {
            key: 'sizeFit',
            label: this.$string('product.form.size_fit'),
            value: '',
          },
          {
            key: 'featuresDetails',
            label: this.$string('product.form.features_details'),
            value: '',
          },
          {
            key: 'styledWith',
            label: this.$string('product.form.styled_with'),
            value: '',
          },
        ]
      }

      const data = []

      if (this.product.careGuide) {
        data.push(
          {
            key: 'careGuide',
            label: this.$string('product.form.care'),
            value: this.product.careGuide,
            type: 'text',
          },
        )
      }

      if (this.product.sizeFit) {
        data.push(
          {
            key: 'sizeFit',
            label: this.$string('product.form.size_fit'),
            value: this.product.sizeFit,
            type: 'text',
          },
        )
      }

      if (this.product.featuresDetails) {
        data.push(
          {
            key: 'featuresDetails',
            label: this.$string('product.form.features_details'),
            value: JSON.parse(this.product.featuresDetails.value),
            type: 'list',
          },
        )
      }

      if (this.product.styledWith) {
        data.push(
          {
            key: 'styledWith',
            label: this.$string('product.form.styled_with'),
            value: this.product.styledWith,
            type: 'styled-with',
          },
        )
      }

      return data
    },

    /**
     * Compute message from product tags
     * @returns {Boolean, String}
     */
    messageTag() {
      if (!this.product) {
        return false
      }

      const tag = this.product.tags.find((data) => data.includes('message:'))

      if (!tag) {
        return false
      }

      return tag.split(':')[1]
    },

    /**
     * Compute if product should notify Back-in-stock
     * @returns {Boolean}
     */
    notifyBis() {
      if (!this.product || !this.liquid.backInStockEnabled) {
        return false
      }

      const tag = this.product.tags.every((data) => {
        return data.toLowerCase() !== 'no-bis'
      })

      if (!tag) {
        return false
      }

      return true
    },

    /**
     * Compute a reactive gift card price
     */
    giftCardPrice() {
      let giftCardPrice = false

      if (this.giftCard) {
        giftCardPrice = this.variant?.price?.amount ||
          this.product?.price?.amount || this.liquid.price
      }

      return giftCardPrice
    },
  },

  watch: {

    /**
     * Watch currentHandle for update to load new product.
     */
    currentHandle() {
      this.queryProduct(this.currentHandle)
    },

    /**
     * Watch for product change.
     */
    product() {
      this.masterTitle = this.product.title.split(' - ')[0].trim()
      this.gender = this.product.tags.find((tag) => tag.includes('gender:'))

      if (!this.hasSwatches || this.swatchProducts.length) {
        return
      }

      this.querySwatchProducts()
    },

    /**
     * Watch for updated swatch products.
     */
    swatchProducts() {
      this.getSwatchData()

      if (this.mainImageLoaded) {
        this.preloadVariantImages()
      }
    },

    /**
     * Watch for when swatches first load that set the product object ready
     * state.
     */
    swatches() {
      this.setProductObjectReady()
    },
  },

  mounted() {
    this.setLockedStatus()
    this.queryProduct()
  },

  methods: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setComponentState: 'components/setState',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Toggle Size Guide overlay
     */
    toggleSizeGuide() {
      if (this.product.sizeGuideId) {
        cnvs.EventBus.emit('size-guide:toggle', {
          sizeGuide: this.product.sizeGuideId,
        })

        this.toggleOverlay({
          ignoreDismissed: true,
          namespace: 'sizeGuide',
        })
      }
    },

    /**
     * Expand product details.
     * @param {String}
     */
    expandDetails(details) {
      if (this.expandedDetails === details) {
        this.expandedDetails = ''
        return
      }

      this.expandedDetails = details
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false) {
        return
      }

      if (this.product.handle === handle) {
        this.state = 'ready'
        return
      }

      try {
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: productWithMetafields,
          variables: {
            country: cnvs.store.country,
            handle,
            namespace: 'descriptors',
            modelSizeCaptionKey: 'model_size_caption',
            preorderShippingDateKey: 'preorder_shipping_date',
            washKey: 'machine_wash',
            fabricKey: 'fabric',
            importedKey: 'imported',
            careGuideKey: 'care_guide_content',
            sizeFitKey: 'size_fit',
            featuresDetailsKey: 'features_details',
            styledWithKey: 'styled_with',
            sizeGuideKey: 'size_guide',
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        this.$emit('update-request-object', {
          response: formatProduct(response),
          type: 'product',
        })

        this.state = 'ready'

        // If it has no swatches then don't wait to set object ready state.
        if (!this.hasSwatches) {
          this.setProductObjectReady()
        }

      } catch (error) {
        throw new Error('Failed to load product', error)
      }
    },

    /**
     * Set the component active state for the product object.
     * - Used to load the PDP after product object is fetched
     */
    setProductObjectReady() {
      this.setComponentState({
        handle: 'product-object',
        state: 'active',
      })
    },

    /**
     * Load products from GraphQL and sort currently active product to front.
     */
    async querySwatchProducts() {
      let query = `(title:'${this.masterTitle}'*)`

      if (this.gender) {
        query += ` AND (tag:'${this.gender}')`
      }

      try {
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: productsWithMetafields,
          variables: {
            country: cnvs.store.country,
            query,
            namespace: 'descriptors',
            modelSizeCaptionKey: 'model_size_caption',
            preorderShippingDateKey: 'preorder_shipping_date',
            washKey: 'machine_wash',
            fabricKey: 'fabric',
            importedKey: 'imported',
            careGuideKey: 'care_guide_content',
            sizeFitKey: 'size_fit',
            featuresDetailsKey: 'features_details',
            styledWithKey: 'styled_with',
            sizeGuideKey: 'size_guide',
          },
        })

        if (!response.products.edges.length) {
          return
        }

        const tmpSwatches = []

        response.products.edges.forEach((product) => {
          const data = { product: product.node }

          if (this.isProductMatch(data.product.title)) {
            tmpSwatches.push(formatProduct(data))
          }
        })

        const activeProduct = tmpSwatches.find((product) => {
          return this.product.id === product.id
        })

        const index = tmpSwatches.indexOf(activeProduct)

        tmpSwatches.splice(index, 1)
        tmpSwatches.unshift(activeProduct)

        this.swatchProducts = tmpSwatches

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      if (variant.selectedOptions.find(option => option.name === 'Size')) {
        const selectedSize = variant.selectedOptions.find(option => option.name === 'Size').value
        this.selectedSizeIndex = this.product.options.find(option => option.name === 'Size').values.indexOf(selectedSize)
      }

      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      const url = variant?.id
        ? `${location.origin}${location.pathname}?variant=${variant.id}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Get list of all relevant swatch images.
     * sorted by Product ID.
     */
    getSwatchData() {
      this.swatches = this.swatchProducts.map((product) => {
        const color = product.options.find((option) => {
          return option.name.toLowerCase() === 'color'
        })

        if (!color) {
          return false
        }

        const location = window.cnvs.urls.files.split('placeholder')[0]
        const key = handleize(color.values[0])

        const fabricTag = product.tags.find((tag) => {
          const clean = tag.toLowerCase()

          return clean.includes('fabric')
        })

        let swatchSrc = `${location}color`

        if (fabricTag) {
          const fabric = fabricTag.split(':')[1]
            .split(' ')
            .join('-')
            .toLowerCase()
            .trim()

          swatchSrc = `${swatchSrc}-${fabric}`
        }

        swatchSrc = `${swatchSrc}-${key}_32x10.png`

        const isSale =
          Boolean(product.compareAtPrice.amount &&
            product.compareAtPrice.amount > product.price.amount)

        return {
          available: product.available,
          id: product.id,
          image: swatchSrc,
          name: color.values[0],
          sale: isSale,
        }
      })
    },

    /**
     * Update PDP with data from newly selected swatch.
     * @param {Object} swatch - Selected Swatch.
     */
    handleSwatchUpdate(swatch) {
      const product = this.swatchProducts.find((data) => {
        return data.id === swatch.id
      })

      if (!product) {
        return
      }

      this.$emit('update-request-object', {
        response: product,
        type: 'product',
      })

      this.$emit('update-request-object', {
        response: product.variants[0],
        type: 'variant',
      })

      const url = product.variants[this.selectedSizeIndex].id
        ? `${product.url}?variant=${product.variants[this.selectedSizeIndex].id}`
        : `${product.url}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Preload the first image of each swatch variant.
     */
    preloadVariantImages() {
      const currentImg = this.$refs.featuredImage.$refs.responsiveImage.currentSrc
      const lastIndex = currentImg.lastIndexOf('_')
      const data = currentImg.slice(lastIndex)
      const size = data.split('.')[0]

      this.swatchProducts.forEach((product) => {
        const src = product.featuredImage.src
        const productLastIndex = src.lastIndexOf('.')
        const productData = [
          src.slice(0, productLastIndex),
          src.slice(productLastIndex),
        ]

        const url = productData.join(size)

        new Image().src = url
      })
    },

    /**
     * Get tag value.
     * @param {String}
     * @returns {Boolean, String} - value of tag
     */
    readTag(id) {
      if (!this.product) {
        return false
      }

      const tag = this.product.tags.find((data) => {
        const label = data.split(':')[0]
        return label === id
      })

      if (!tag) {
        return false
      }

      const tagData = tag.split(':')

      return tagData[tagData.length - 1]
    },

    /**
     * Add failed attribute to swatch images that 404.
     * @param {Object} swatch
     */
    swatchLoaded(swatch) {
      swatch.loaded = true
    },

    /**
     * Determine if queried product matches current product.
     * @param {String}
     * @returns {Boolean}
     */
    isProductMatch(title) {
      const masterTitle = this.masterTitle.toLowerCase()
      const newTitle = title.toLowerCase().split(' - ')[0].trim()

      return masterTitle === newTitle
    },

    /**
     * Set locked status based off LocalStorage.
     */
    setLockedStatus() {
      const unlocked = JSON.parse(localStorage.getItem('unlockedPages'))

      if (
        !unlocked &&
        this.liquid.pageLock.password
      ) {
        this.locked = true
        return
      }

      this.locked =
        this.liquid.pageLock.password &&
        !unlocked.includes(window.location.pathname)

    },
  },
}
</script>
